import axios from 'axios';
import { API_AUTH } from '@/const/uri';

const AUTH_REFRESH_MARGIN_MS = 5 * 60 * 1000;

let refreshPromise = null;

function clearAuthToken() {
  localStorage.removeItem('user_token');
  localStorage.removeItem('user_token_expired');
}

function getTokenExpiredAt() {
  const rawValue = localStorage.getItem('user_token_expired');
  if (!rawValue) return null;

  const timestamp = new Date(rawValue).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

function isAuthRequest(url) {
  const requestUrl = String(url || '');
  return requestUrl.includes('/api/auth/login/')
    || requestUrl.includes('/api/auth/client-login/')
    || requestUrl.includes('/api/auth/refresh-token/');
}

async function refreshAuthToken() {
  if (refreshPromise) return refreshPromise;

  const token = localStorage.getItem('user_token');
  if (!token) return null;

  refreshPromise = axios({
    method: 'POST',
    url: API_AUTH.refreshToken,
    headers: {
      Authorization: `Token ${token}`,
    },
    skipAuthRefresh: true,
    skipAuthRedirect: true,
  })
    .then(response => {
      const data = response.data?.data || response.data;
      if (!data?.token) return null;

      localStorage.setItem('user_token', data.token);
      localStorage.setItem('user_token_expired', data.token_expired);
      return data.token;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

axios.interceptors.request.use(async config => {
  if (config.skipAuthRefresh || isAuthRequest(config.url)) {
    return config;
  }

  const token = localStorage.getItem('user_token');
  if (!token) return config;

  const expiredAt = getTokenExpiredAt();
  if (!expiredAt || expiredAt - Date.now() > AUTH_REFRESH_MARGIN_MS) {
    return config;
  }

  try {
    const refreshedToken = await refreshAuthToken();
    if (refreshedToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Token ${refreshedToken}`;
    }
  } catch (error) {
    clearAuthToken();
    if (!config.skipAuthRedirect) {
      window.location.href = '/client/login';
    }
    throw error;
  }

  return config;
});

axios.interceptors.response.use(
  response => response,
  async error => {
    if (error.response?.status === 401) {
      const originalRequest = error.config || {};

      if (!originalRequest._retry && !originalRequest.skipAuthRefresh && !isAuthRequest(originalRequest.url)) {
        originalRequest._retry = true;
        try {
          const refreshedToken = await refreshAuthToken();
          if (refreshedToken) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Token ${refreshedToken}`;
            return axios(originalRequest);
          }
        } catch (refreshError) {
          error = refreshError;
        }
      }

      const skipAuthRedirect = originalRequest.skipAuthRedirect;
      clearAuthToken();

      const isLoginRequest = originalRequest.url?.includes('/login');
      if (!isLoginRequest && !skipAuthRedirect) {
        window.location.href = '/client/login';
      }
    }
    return Promise.reject(error);
  }
);

export default axios;
